import * as React from 'react';
import { ViewportProps } from 'react-map-gl';
import { animated, useSpring } from 'react-spring';
import { useMeasure } from 'react-use';
import { HexagonLayer } from '@deck.gl/aggregation-layers';
import DeckGL from '@deck.gl/react';
import { PhongMaterial } from '@luma.gl/core';
import turf_distance from '@turf/distance';
import cx from 'classnames';
import { max, scaleLinear, scaleThreshold } from 'd3';
import { cellToBoundary, latLngToCell } from 'h3-js';
import { Moment } from 'moment-timezone';

import Map from 'src/components/Map/Map';
import { mapbox } from 'src/config';
import { getBounds } from 'src/util/map';
import ActivityMapHexDetails from './ActivityMapHexDetails';
import ActivityMapLegend from './ActivityMapLegend';
import CustomColumnLayer from './CustomColumnLayer';
import { pointToHex } from './H3HexagonAggregator';

import styles from './ActivityMap.module.scss';

export interface ActiveHex {
  hexId: string;
  points: ActivityMapData[];
}

export interface ActivityMapData {
  conversation_id: number;
  conversation_time: Moment;
  lng_lat: [number, number];
  num_highlights: number;
  num_highlights_recent: number;
  latest_activity: Moment;
  title: string;
}

type ActivityMapDataWithHex = {
  h3HexId: string;
} & ActivityMapData;

interface Props {
  activityMapData: ActivityMapData[];
  width: number;
  height: number;
  minZoom?: number;
  maxZoom?: number;
  mapStyle?: string | Record<string, unknown>;
  className?: string | undefined;
  initialLngLat?: [number, number];
  activeHex: ActiveHex | undefined;
  onChangeActiveHex: (activeHex: ActiveHex | undefined) => void;
  recentCutoffDate: Moment;
}

export interface MapChildProps extends ViewportProps {
  onViewportChange: (viewport: ViewportProps) => void;
}

/**
 * Given an h3 hexagon resolution, compute the radius in meters of a single hexagon
 */
function getRadiusFromH3Hex(lngLat: [number, number], h3Resolution: number) {
  const hexId = latLngToCell(lngLat[1], lngLat[0], h3Resolution);
  // need `true` to get [lng, lat] response
  const vertices = cellToBoundary(hexId, true);

  // vertices 0 and 3 are opposite each other in a hexagon
  const diameter = turf_distance(vertices[0], vertices[3], {
    units: 'meters',
  });

  return diameter / 2;
}

const DEFAULT_ZOOM = 4;

const DeckGLOverlay = ({
  activityMapData,
  onClick,
  viewState,
  activeHex,
  recentCutoffDate,
  debug,
}: {
  activityMapData: ActivityMapData[];
  onClick: (h3HexId: string, points: ActivityMapData[]) => void;
  viewState: ViewportProps;
  activeHex: string | undefined | null;
  recentCutoffDate: Moment;
  debug?: [[number, number], [number, number]];
}) => {
  const layers = [];

  const elevationScale = scaleLinear()
    .domain([11.5, 13])
    .range([2000, 25])
    .clamp(true);

  const opacityScale = scaleLinear()
    .domain([11.5, 13])
    .range([1, 0.5])
    .clamp(true);

  // scale from viewState.zoom levels to h3 Hexagon resolution levels
  const hexResolutionScale = scaleThreshold()
    .domain([5.8, 7, 8.4, 10.1])
    .range([4, 5, 6, 7, 8]);
  const hexResolution = hexResolutionScale(viewState.zoom ?? DEFAULT_ZOOM);

  // compute radius based on h3 resolution
  const radius = activityMapData[0]
    ? getRadiusFromH3Hex(activityMapData[0].lng_lat, hexResolution)
    : 100;

  // create a new aggregator for the resolution. note we memoize it since the
  // hexagon layer does a === check to see if it needs to re-aggregate.
  const hexagonAggregator = React.useMemo(
    () => pointToHex(hexResolution),
    [hexResolution]
  );

  // draw any debug points if we have them
  if (debug) {
    const debugLayer = new HexagonLayer({
      id: 'debug-icon-layer',
      data: debug,
      radius,
      getPosition: (d: [number, number]) => d,
      getFillColor: [160, 160, 180, 200],
    });

    layers.push(debugLayer);
  }

  const hexLayer = new HexagonLayer({
    id: 'activity-hex-layer',
    data: activityMapData,
    extruded: true,
    pickable: true,
    autoHighlight: true,

    radius,
    hexagonAggregator,

    elevationScale: elevationScale(viewState.zoom ?? DEFAULT_ZOOM),
    opacity: opacityScale(viewState.zoom ?? DEFAULT_ZOOM),
    colorDomain: [0, 2],

    // note I just shoehorn these to get a value to use in the shader
    // which reads from the subLayerProps below
    colorRange: [
      [0, 0, 0],
      [255, 0, 0],
      [0, 255, 0],
    ],

    getFillColor: [0, 0, 0],
    // getElevation: (d: any) => 100,
    getColorValue: (points: ActivityMapDataWithHex[]) => {
      const values = points.map((d) => {
        if (d.h3HexId === activeHex) {
          return 2;
        }

        return d.latest_activity.isAfter(recentCutoffDate) ? 1 : 0;
      });
      return max(values);
    },
    // colorAggregation: 'MAX',
    getPosition: (d: ActivityMapDataWithHex) => d.lng_lat,
    elevationDomain: [0, 10000],
    getElevationWeight: () => 1,
    elevationLowerPercentile: 0,
    elevationAggregation: 'SUM',
    onClick: (info: any) => {
      const points: ActivityMapDataWithHex[] = info.object.points;
      const h3HexId = points[0].h3HexId;
      onClick(h3HexId, points);
    },

    // make the material brighter
    material: new PhongMaterial({
      ambient: 1.0,
      diffuse: 1.0,
      shininess: 3000,
    }),
    updateTriggers: {
      radius: [radius],
      hexagonAggregator: [hexResolution],
    },

    // use our custom columns instead of hexagons
    _subLayerProps: {
      'hexagon-cell': {
        type: CustomColumnLayer,
        diskResolution: 30, // roughly circular
        radius: radius * 0.85, // shrink so they don't overlap

        // not recent
        color1: [120, 110, 200],
        baseColor1: [120, 110, 160],

        // recent
        color2: [65, 44, 203],
        baseColor2: [75, 59, 223, 255],

        // on hover
        highlightColor: [0, 140, 140, 255],
        highlightColorBase: [0, 120, 120, 255],

        // on toggle
        toggleColor: [0, 130, 130, 255],
        toggleColorBase: [0, 110, 110, 255],
      },
    },
  });
  layers.push(hexLayer);
  return <DeckGL viewState={viewState} layers={layers} />;
};

const ActivityMapPanel: React.FC<
  Pick<Props, 'activeHex' | 'onChangeActiveHex' | 'recentCutoffDate'>
> = ({ activeHex, onChangeActiveHex, recentCutoffDate }) => {
  const handleClearActive = () => {
    onChangeActiveHex(undefined);
  };

  // show legend or show active items
  const showActive = activeHex && activeHex.points.length;

  // measure the height for when showing active (unfortunately doesn't include padding)
  const [ref, rect] = useMeasure();
  const padding = showActive ? 16 : 8;

  // set explicit widths for the two types (active and legend)
  const width = showActive ? 250 : 121;

  // set explicit max height to allow nicer animation when overflowing
  const maxHeight = 300;
  const height = Math.min(
    maxHeight,
    // active uses dynamic height + padding, legend uses hardcoded
    // note that hardcoded allows first render to look right without animation
    showActive ? rect.height + 2 * padding : 127
  );

  // use a spring to animate changes to height and width
  const props = useSpring({ height, width });

  // explicitly set inner box width to handle scrollbar and prevent text
  // wrapping from changing during animation
  const innerStyle =
    showActive && height === maxHeight ? { width: '203px' } : undefined;

  return (
    <animated.div
      className={cx(styles.overlayBox, 'card rounded shadow-sm')}
      style={{ ...props }}
    >
      <div
        className={
          styles.overlayBoxScroller
        } /* scroller must be nested so scrollbar gets clipped */
      >
        <div
          className={cx({
            [styles.overlayBoxActive]: showActive,
            [styles.overlayBoxLegend]: !showActive,
          })} /* these divs add padding */
        >
          <div
            ref={ref}
            className={
              styles.overlayBoxInner
            } /* this is the div that is measured */
            style={innerStyle}
          >
            {showActive ? (
              <ActivityMapHexDetails
                points={(activeHex as any).points} // typescript not smart enough to know we don't need to check for activeHex
                onClear={handleClearActive}
                recentCutoffDate={recentCutoffDate}
              />
            ) : (
              <ActivityMapLegend />
            )}
          </div>
        </div>
      </div>
    </animated.div>
  );
};

export const ActivityMap: React.FC<Props> = ({
  width,
  height,
  minZoom,
  activityMapData,
  maxZoom,
  mapStyle = mapbox.mapStyles.lvnLighter,
  initialLngLat,
  className,
  activeHex,
  onChangeActiveHex,
  recentCutoffDate,
}) => {
  const pitch = 45;
  const bearing = 0;
  const initialZoom = 11.5;

  // on click, toggle on and off the active hex column
  // since the aggregation happens in the layer, we need to keep track of the points here too.
  const handleClick = (h3HexId: string, points: ActivityMapData[]) => {
    onChangeActiveHex(
      activeHex && activeHex.hexId === h3HexId
        ? undefined
        : { hexId: h3HexId, points }
    );
  };

  if (!initialLngLat) {
    return null;
  }

  // find the bounds using conversation locations
  const bounds = getBounds(activityMapData);

  return (
    <div
      className="position-relative"
      style={{ width: `${width}px`, height: `${height}px` }}
    >
      <Map
        initialLngLat={initialLngLat}
        width={width}
        height={height}
        initialPitch={pitch}
        initialBearing={bearing}
        initialZoom={initialZoom}
        mapStyle={mapStyle}
        minZoom={minZoom}
        maxZoom={maxZoom}
        bounds={bounds}
        className={cx(className)}
      >
        {({ ...viewport }) => (
          <DeckGLOverlay
            activityMapData={activityMapData}
            onClick={handleClick}
            viewState={viewport as ViewportProps}
            activeHex={activeHex && activeHex.hexId}
            recentCutoffDate={recentCutoffDate}
            // debug={bounds}
          />
        )}
      </Map>
      <ActivityMapPanel
        recentCutoffDate={recentCutoffDate}
        activeHex={activeHex}
        onChangeActiveHex={onChangeActiveHex}
      />
    </div>
  );
};

export default ActivityMap;
